import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Wallet } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { API, useAuth } from "@/App";

const fmt = (n) => `GHS ${Number(n || 0).toFixed(2)}`;

// iOS-style settings row: auto-pay the service charge from settled wallet funds.
export default function AutoPayToggle({ enabled: initial, onChanged, compact = false, testId = "service-charge-autopay" }) {
  const { token } = useAuth() || {};
  const [enabled, setEnabled] = useState(!!initial);
  const [busy, setBusy] = useState(false);
  const [justPaid, setJustPaid] = useState(null);
  useEffect(() => { setEnabled(!!initial); }, [initial]);
  useEffect(() => {
    if (!justPaid) return undefined;
    const t = setTimeout(() => setJustPaid(null), 6000);
    return () => clearTimeout(t);
  }, [justPaid]);

  const toggle = async (v) => {
    setEnabled(v); setBusy(true);
    try {
      const { data } = await axios.put(`${API}/pro/wallet/auto-pay-service-charge?token=${token}`, { enabled: v });
      setEnabled(!!data.enabled);
      if (data.paid_now?.amount) setJustPaid(data.paid_now);
      window.dispatchEvent(new CustomEvent("yorm:wallet-updated", { detail: { type: "autopay_toggled" } }));
      onChanged?.(data);
    } catch (e) {
      setEnabled(!v);
      toast.error(e?.response?.data?.detail || "Could not update auto-pay");
    } finally {
      setBusy(false);
    }
  };

  return (
    <label className={`flex items-center gap-3 ${compact ? "px-4 py-3" : "px-4 py-3.5"} rounded-2xl bg-white cursor-pointer select-none`} data-testid={testId}>
      <span className="w-9 h-9 rounded-full bg-[#4F988E]/10 text-[#4F988E] flex items-center justify-center shrink-0"><Wallet size={17} /></span>
      <span className="flex-1 min-w-0">
        <span className="block text-[15px] font-medium text-gray-900">Auto-pay from wallet</span>
        <span className="block text-[12px] text-gray-500 leading-snug">
          {enabled ? "What you owe is paid from your wallet as soon as funds settle." : "Pay what you owe automatically when funds settle."}
        </span>
        {justPaid && (
          <span className="mt-1 inline-flex items-start gap-1 text-[12px] font-semibold text-[#1F8F3F]" data-testid={`${testId}-paid-now`}>
            <Check size={12} strokeWidth={3} className="mt-[3px] shrink-0" /> <span>{fmt(justPaid.amount)} paid from your wallet just now{justPaid.owed >= 0.01 ? ` · ${fmt(justPaid.owed)} still owed` : " · all settled"}</span>
          </span>
        )}
      </span>
      <Switch checked={enabled} disabled={busy} onCheckedChange={toggle} className="data-[state=checked]:bg-[#34C759]" data-testid={`${testId}-switch`} aria-label="Auto-pay service charge from wallet" />
    </label>
  );
}
